import { axiosJSONInstance } from '@/config';
import type { GlobalSearchResult, SearchCategory } from '@/types/globalSearch';

/* ─── Per-module config ─── */
interface ModuleConfig {
  category: SearchCategory;
  endpoint: string;
  maxResults: number;
  /** Extra results to fetch for client-side re-ranking */
  fetchSize: number;
  /** Permission required to search this module (from PermissionName enum) */
  requiredPermission: string;
  /** Extract array of items from the raw API response */
  extractItems: (data: any) => any[];
  /** Map a single item to a GlobalSearchResult (receives raw item + query for scoring) */
  mapItem: (item: any, query: string) => GlobalSearchResult;
  /** Fields to search on when query looks like a code/ID */
  codeSearchAttributes?: string[];
}

/* ─── Query type detection ─── */
const CODE_PATTERN = /^(bx|boxy|bxo|bxre|bxpu|bxrto|bxcl|bxpc|bxtr)/i;
// Matches "prefix-123" or "prefix_123" style IDs, but NOT pure numbers (phone numbers)
const ID_LIKE_PATTERN = /^[a-zA-Z]+[-_]\d+/;

function isCodeQuery(query: string): boolean {
  return CODE_PATTERN.test(query) || ID_LIKE_PATTERN.test(query);
}

/* ─── Relevance scoring ─── */
function scoreResult(result: GlobalSearchResult, query: string): number {
  const q = query.toLowerCase();
  const primary = (result.primary || '').toLowerCase();
  const tag = (result.tag || '').toLowerCase();
  const secondary = (result.secondary || '').toLowerCase();

  // Exact match on tag (custom_id) — highest priority
  if (tag && tag === q) return 100;
  // Exact match on primary (platform_code)
  if (primary === q) return 95;
  // Tag starts with query
  if (tag && tag.startsWith(q)) return 90;
  // Tag contains query
  if (tag && tag.includes(q)) return 85;
  // Primary starts with query
  if (primary.startsWith(q)) return 80;
  // Primary contains query
  if (primary.includes(q)) return 70;
  // Secondary contains query (name/phone match)
  if (secondary.includes(q)) return 50;
  // Fuzzy match (Meilisearch returned it but no direct text match)
  return 10;
}

/**
 * How many rows the "View all results" page asks for per category.
 *
 * 🔴 THE DROPDOWN'S CAPS ARE A PREVIEW BUDGET, NOT A RESULT LIMIT — and the
 * full-results page was reusing them. `maxResults` (5 orders / 3 elsewhere)
 * exists so a type-ahead panel stays readable; the page that exists precisely
 * to show everything was slicing to the same 5 while printing the real total
 * beside it. "View all results (37)" listing five rows. (BOXY-234.)
 *
 * ⚠️ A PAGE SIZE, NOT "ALL". Unbounded would mean fetching every match a
 * merchant has ever had for a one-character query. 50 is a screenful of
 * scrolling and one request; real pagination is a bigger feature than this
 * ticket asks for, and the total is still displayed so nobody is misled about
 * how much exists.
 */
const FULL_PAGE_SIZE = 50;

const MODULE_CONFIGS: ModuleConfig[] = [
  {
    category: 'order',
    endpoint: '/merchants/orders/search',
    maxResults: 5,
    fetchSize: 15, // Fetch more for smart re-ranking
    requiredPermission: 'order_and_shipment',
    extractItems: (data) => data?.object?.items ?? data?.object?.data ?? [],
    codeSearchAttributes: ['platform_code', 'custom_id'],
    mapItem: (item, query) => {
      const raw = item.status?.merchant_title ?? item.status?.title ?? '';
      const statusText = raw.includes('.') ? raw.split('.').pop() : raw;
      return {
        category: 'order',
        id: item.uid,
        primary: item.platform_code,
        secondary: [item.full_name, item.phone].filter(Boolean).join(' — '),
        href: `/orders/${item.uid}`,
        tag: item.custom_id || undefined,
        statusText: statusText || undefined,
        statusSlug: item.status?.slug,
      };
    },
  },
  {
    category: 'pickup',
    endpoint: '/merchants/pick-ups/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'order_and_shipment',
    extractItems: (data) => data?.object?.data ?? data?.object?.items ?? [],
    codeSearchAttributes: ['platform_code'],
    mapItem: (item) => ({
      category: 'pickup',
      id: item.uid,
      primary: item.platform_code ?? item.uid,
      secondary: item.pick_up_address?.title ?? item.pick_up_address?.address_text ?? '',
      href: `/pickups/${item.uid}`,
      statusText: item.status?.title,
      statusSlug: item.status?.slug,
    }),
  },
  {
    category: 'remittance',
    endpoint: '/merchants/remittance/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'finance',
    extractItems: (data) => data?.object?.data ?? data?.object?.items ?? [],
    codeSearchAttributes: ['platform_code'],
    mapItem: (item) => ({
      category: 'remittance',
      id: item.uid,
      primary: item.platform_code ?? item.uid,
      secondary: item.status?.title ?? '',
      href: `/finance/details/${item.uid}`,
      statusText: item.status?.title,
      statusSlug: item.status?.slug,
    }),
  },
  {
    category: 'rto',
    endpoint: '/merchants/rto/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'order_and_shipment',
    extractItems: (data) => data?.object?.data ?? data?.object?.items ?? [],
    codeSearchAttributes: ['platform_code'],
    mapItem: (item) => ({
      category: 'rto',
      id: item.uid,
      primary: item.platform_code ?? item.uid,
      secondary: item.pick_up_address?.title ?? item.status?.title ?? '',
      href: `/pickups/rto/${item.uid}`,
      statusText: item.status?.title,
      statusSlug: item.status?.slug,
    }),
  },
  {
    category: 'contact',
    endpoint: '/merchants/contacts/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'dashboard',
    extractItems: (data) => data?.object?.data ?? data?.object?.items ?? [],
    mapItem: (item) => ({
      category: 'contact',
      id: item.uid,
      primary: item.full_name ?? item.platform_code ?? item.uid,
      secondary: [item.phone, item.address_text].filter(Boolean).join(' — '),
      href: `/settings/address-book/${item.uid}/edit`,
    }),
  },
  {
    category: 'claim',
    endpoint: '/merchants/claims/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'view_claims',
    // The claims /search endpoint returns hits under `object.items` (Meilisearch
    // envelope), NOT `object.data` (that's the list endpoint). Reading only
    // `data` silently dropped every claim from global search. Match the other
    // modules: prefer `items`, fall back to `data`.
    extractItems: (data) => data?.object?.items ?? data?.object?.data ?? [],
    // 🔴 AN ORDER CODE IS A LEGITIMATE WAY TO LOOK FOR A CLAIM. Restricting a
    // code-like query to the claim's OWN platform_code meant pasting a BXO-
    // order code returned nothing from the claims module — which reads as "the
    // claim is not in search" even though it is indexed and the API is healthy.
    // `order.platform_code` is a searchable attribute on the claims index, so
    // ask for it. `platform_code_search` is the sliding-ngram twin, included so
    // a PARTIAL code matches: `platform_code` has typo tolerance disabled.
    codeSearchAttributes: ['platform_code', 'platform_code_search', 'order.platform_code'],
    mapItem: (item) => ({
      category: 'claim',
      id: item.uid,
      primary: item.platform_code ?? item.uid,
      secondary: [item.type, item.order?.platform_code].filter(Boolean).join(' — '),
      href: `/claims/${item.uid}`,
      statusText: item.status?.title,
      statusSlug: item.status?.slug,
    }),
  },
  {
    category: 'transaction',
    endpoint: '/merchants/transactions/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'finance',
    extractItems: (data) => data?.object?.items ?? data?.object?.data ?? [],
    codeSearchAttributes: ['platform_code', 'platform_code_search'],
    mapItem: (item) => {
      const amount = item.amount != null ? `${Number(item.amount).toLocaleString()} IQD` : null;
      const orderCode = item.order?.platform_code;
      return {
        category: 'transaction',
        id: item.uid,
        primary: item.platform_code ?? item.uid,
        secondary: [item.subject, amount, orderCode].filter(Boolean).join(' — '),
        href: `/finance?tab=transactions&q=${encodeURIComponent(item.platform_code ?? item.uid)}`,
        statusText: item.status?.title ?? item.status?.slug,
        statusSlug: item.status?.slug,
      };
    },
  },
  {
    category: 'product',
    endpoint: '/merchants/product-catalog/search',
    maxResults: 3,
    fetchSize: 5,
    requiredPermission: 'order_and_shipment',
    extractItems: (data) => {
      if (Array.isArray(data?.object)) return data.object;
      return data?.object?.data ?? data?.object?.items ?? [];
    },
    mapItem: (item) => ({
      category: 'product',
      id: item.uid,
      primary: item.title,
      secondary: [
        item.category,
        item.price != null ? `${Number(item.price).toLocaleString()} IQD` : null,
      ]
        .filter(Boolean)
        .join(' — '),
      href: `/settings/product-catalog/${item.uid}/edit`,
    }),
  },
];

export { MODULE_CONFIGS };

/* ─── Search a single module ─── */
async function searchModule(
  config: ModuleConfig,
  query: string,
  /** True on the "View all results" page — see FULL_PAGE_SIZE. */
  full = false,
): Promise<{ category: SearchCategory; results: GlobalSearchResult[]; total: number }> {
  const params = new URLSearchParams({
    query,
    perPage: (full ? FULL_PAGE_SIZE : config.fetchSize).toString(),
    page: '1',
  });

  // For code-like queries, restrict search to identifier fields to reduce noise
  const isCode = isCodeQuery(query);
  if (isCode && config.codeSearchAttributes) {
    config.codeSearchAttributes.forEach((attr) => {
      params.append('search_on_attributes[]', attr);
    });
  }

  // Product catalog uses `q` not `query`
  if (config.category === 'product') {
    params.delete('query');
    params.delete('perPage');
    params.set('q', query);
  }

  const response = await axiosJSONInstance.get(`${config.endpoint}?${params.toString()}`);
  const items = config.extractItems(response.data);
  // The Meilisearch /search envelope frequently reports `total: 0` even when it
  // returns matching `items` (the hit count isn't populated on this endpoint).
  // A literal 0 defeats `?? items.length`, so treat any non-positive/missing
  // total as "unknown" and fall back to the number of items actually returned.
  // Otherwise the dropdown's empty-state gate hides real matches ("No results
  // found" while the order sits in the results).
  const reportedTotal = response.data?.object?.total ?? response.data?.object?.meta?.total;
  const total =
    typeof reportedTotal === 'number' && reportedTotal > 0 ? reportedTotal : items.length;

  // Map all fetched items to results
  const allResults = items.map((item: any) => config.mapItem(item, query));

  // Score and sort by relevance
  const scored = allResults.map((r: GlobalSearchResult) => ({
    result: r,
    score: scoreResult(r, query),
  }));
  scored.sort((a: { score: number }, b: { score: number }) => b.score - a.score);

  // For code-like queries, filter out low-relevance fuzzy matches (noise reduction)
  // Keep only results where the query actually appears in the result text
  const filtered = isCode
    ? scored.filter((s: { score: number }) => s.score >= 50)
    : scored;

  // 🔴 NO FALLBACK TO NOISE. This used to be `filtered.length > 0 ? filtered
  // : scored` — "if strict filtering removed everything, fall back to top
  // results by score". Which means: precisely when a module has NOTHING
  // relevant, show its irrelevant matches instead. A merchant searching
  // BXPU-262410372149 (a pickup) was shown an unrelated ORDER whose custom_id
  // happens to contain the letters BXPU via ngram matching, score 10, dressed
  // up as a result — while the actual pickup sat in its own group. The owner's
  // words for this behaviour were not kind, and they were correct.
  //
  // A score below 50 means the query appears NOWHERE the merchant can see —
  // not in the code, not in the tag, not in the name/phone line. Showing it
  // teaches merchants the search lies. An empty module group is the truthful
  // answer, and the module simply does not render, which is what empty means.
  //
  // ⚠️ This drops ONLY score-10 fuzz. A genuine substring hit on the visible
  // fields scores 50–100 and survives the filter on its own merits.
  const finalList = filtered;

  // Flag exact/near-exact matches
  // In full mode the ranking still applies — only the truncation goes. The
  // merchant gets every row we fetched, best match first.
  const ranked = full ? finalList : finalList.slice(0, config.maxResults);
  const results = ranked.map((s: { result: GlobalSearchResult; score: number }) => ({
    ...s.result,
    // Mark as exact match if score >= 85 (exact or starts-with on tag/primary)
    isExactMatch: s.score >= 85,
  }));

  // For code queries, report filtered count as total when it's lower
  const adjustedTotal = isCode && filtered.length < total
    ? Math.max(filtered.length, results.length)
    : total;

  return {
    category: config.category,
    results,
    total: adjustedTotal,
  };
}

/* ─── Search all modules in parallel (filtered by user permissions) ─── */
export async function globalSearch(query: string, userPermissions?: string[], full = false) {
  // Only search modules the user has permission to access
  const allowedModules = userPermissions
    ? MODULE_CONFIGS.filter((config) => userPermissions.includes(config.requiredPermission))
    : MODULE_CONFIGS;

  const settled = await Promise.allSettled(
    allowedModules.map((config) => searchModule(config, query, full)),
  );

  const groups: { category: SearchCategory; results: GlobalSearchResult[]; total: number }[] = [];

  for (const result of settled) {
    if (result.status === 'fulfilled' && result.value.results.length > 0) {
      groups.push(result.value);
    }
  }

  // Sort groups: groups with high-relevance results first
  groups.sort((a, b) => {
    const aHasExact = a.results.some((r: any) => r.isExactMatch);
    const bHasExact = b.results.some((r: any) => r.isExactMatch);
    if (aHasExact && !bHasExact) return -1;
    if (!aHasExact && bHasExact) return 1;
    return 0;
  });

  return groups;
}
